/**
 * TanStack Query configuration.
 *
 * The retry policy is the decision worth explaining: retrying a 4xx is always
 * wasted work, because the same request will fail the same way. Worse, a
 * retried 403 delays the "you do not have permission" message behind three
 * round trips. Only 5xx and network failures are retried.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.9
 */
import { QueryClient } from '@tanstack/react-query';

import { isApiError } from '../lib/problem';

/** How long a fetched value is served without a background refetch. */
const STALE_TIME_MS = 30_000;

/** Attempts, including the first. */
const MAX_ATTEMPTS = 3;

/**
 * Decides whether a failed request is worth retrying.
 *
 * Exported so the rule can be tested directly rather than through a query.
 */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_ATTEMPTS) return false;

  if (isApiError(error)) {
    /* Status 0 is a network failure — no response reached us, so it is worth
       another attempt. A real 4xx is a settled answer. */
    if (error.status === 0) return true;
    return error.status >= 500;
  }

  return true;
}

/** Builds a client. A function, so each test gets an isolated cache. */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: STALE_TIME_MS,
        retry: shouldRetry,
        /* Refetching on focus is noise for a database people leave open all
           day, and it multiplies load for data that changes slowly. */
        refetchOnWindowFocus: false,
      },
      mutations: {
        /* A mutation is not idempotent. Retrying a failed create can produce
           two records, which is worse than surfacing the error. */
        retry: false,
      },
    },
  });
}
